import * as Marzipano from 'marzipano';

type PitchCallback = (err: null, pitch: number) => void;

interface EulerAngles {
  yaw: number;
  pitch: number;
  roll: number;
}

/**
 * Ported from marzipano/demos/device-orientation/DeviceOrientationControlMethod.js
 * (not included in the main marzipano npm package).
 */
export class DeviceOrientationControlMethod {
  private _dynamics: { yaw: Marzipano.Dynamics; pitch: Marzipano.Dynamics };
  private _deviceOrientationHandler: (data: DeviceOrientationEvent) => void;
  private _previous: Partial<EulerAngles>;
  private _current: Partial<EulerAngles>;
  private _tmp: EulerAngles;
  private _getPitchCallbacks: PitchCallback[];

  // Mixed in by Marzipano.dependencies.eventEmitter() below
  emit!: (event: string, ...args: unknown[]) => void;

  constructor() {
    this._dynamics = {
      yaw: new Marzipano.Dynamics(),
      pitch: new Marzipano.Dynamics(),
    };

    this._deviceOrientationHandler = this._handleData.bind(this);
    this._previous = {};
    this._current = {};
    this._tmp = { yaw: 0, pitch: 0, roll: 0 };
    this._getPitchCallbacks = [];

    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', this._deviceOrientationHandler);
    }
  }

  destroy() {
    if (window.DeviceOrientationEvent) {
      window.removeEventListener('deviceorientation', this._deviceOrientationHandler);
    }
  }

  getPitch(cb: PitchCallback) {
    this._getPitchCallbacks.push(cb);
  }

  private _handleData(data: DeviceOrientationEvent) {
    const previous = this._previous;
    const current = this._current;
    const tmp = this._tmp;

    if (data.alpha == null || data.beta == null || data.gamma == null) return;

    tmp.yaw = Marzipano.util.degToRad(data.alpha);
    tmp.pitch = Marzipano.util.degToRad(data.beta);
    tmp.roll = Marzipano.util.degToRad(data.gamma);

    rotateEuler(tmp, current);

    this._getPitchCallbacks.forEach((callback) => {
      callback(null, current.pitch ?? 0);
    });
    this._getPitchCallbacks.length = 0;

    if (
      previous.yaw != null &&
      previous.pitch != null &&
      previous.roll != null &&
      current.yaw != null &&
      current.pitch != null
    ) {
      this._dynamics.yaw.offset = -(current.yaw - previous.yaw);
      this._dynamics.pitch.offset = current.pitch - previous.pitch;

      this.emit('parameterDynamics', 'yaw', this._dynamics.yaw);
      this.emit('parameterDynamics', 'pitch', this._dynamics.pitch);
    }

    previous.yaw = current.yaw;
    previous.pitch = current.pitch;
    previous.roll = current.roll;
  }
}

function rotateEuler(euler: EulerAngles, result: Partial<EulerAngles>) {
  const ch = Math.cos(euler.yaw);
  const sh = Math.sin(euler.yaw);
  const ca = Math.cos(euler.pitch);
  const sa = Math.sin(euler.pitch);
  const cb = Math.cos(euler.roll);
  const sb = Math.sin(euler.roll);

  const matrix = [
    sh * sb - ch * sa * cb,
    -ch * ca,
    ch * sa * sb + sh * cb,
    ca * cb,
    -sa,
    -ca * sb,
    sh * sa * cb + ch * sb,
    sh * ca,
    -sh * sa * sb + ch * cb,
  ];

  let heading: number;
  let bank: number;
  let attitude: number;

  if (matrix[3] > 0.9999) {
    heading = Math.atan2(matrix[2], matrix[8]);
    attitude = Math.PI / 2;
    bank = 0;
  } else if (matrix[3] < -0.9999) {
    heading = Math.atan2(matrix[2], matrix[8]);
    attitude = -Math.PI / 2;
    bank = 0;
  } else {
    heading = Math.atan2(-matrix[6], matrix[0]);
    bank = Math.atan2(-matrix[5], matrix[4]);
    attitude = Math.asin(matrix[3]);
  }

  result.yaw = heading;
  result.pitch = attitude;
  result.roll = bank;
}

// Mix in emit/addEventListener onto prototype (Marzipano API requirement)
Marzipano.dependencies.eventEmitter(DeviceOrientationControlMethod);

export async function requestDeviceOrientationPermission(): Promise<boolean> {
  const DeviceOrientation = window.DeviceOrientationEvent as typeof DeviceOrientationEvent & {
    requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
  };

  if (typeof DeviceOrientation.requestPermission === 'function') {
    const response = await DeviceOrientation.requestPermission();
    return response === 'granted';
  }

  return 'DeviceOrientationEvent' in window;
}
